import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import SupplierComparison from '../components/SupplierComparison';
import MessageDialog from '../components/MessageDialog';
import { toast } from 'sonner';
import { Loader2, Bookmark, MessageSquare, Trash2, Edit2, GitCompare, Plus, Check, MapPin, BadgeCheck, FileDown } from 'lucide-react';
import { STATUSES, STATUS_BADGE, STATUS_DOT } from '../lib/outreachStatus';

const PRESET_TAGS = [
  'best_price', 'reliable', 'fast_shipping', 'customization',
  'test_later', 'premium', 'high_risk', 'negotiable_moq', 'good_for_samples',
];

const TAG_COLORS = {
  best_price: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  reliable: 'bg-blue-50 text-blue-700 border-blue-200',
  fast_shipping: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  customization: 'bg-violet-50 text-violet-700 border-violet-200',
  test_later: 'bg-slate-50 text-slate-700 border-slate-200',
  premium: 'bg-amber-50 text-amber-700 border-amber-200',
  high_risk: 'bg-rose-50 text-rose-700 border-rose-200',
  negotiable_moq: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  good_for_samples: 'bg-teal-50 text-teal-700 border-teal-200',
};

const Saved = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const pipelineRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [editing, setEditing] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editStatus, setEditStatus] = useState('not_contacted');
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [messageItem, setMessageItem] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/saved-suppliers');
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When entering via bottom-nav Tracker tab (?view=tracker), scroll pipeline into view
  useEffect(() => {
    if (location.search.includes('view=tracker') && pipelineRef.current) {
      pipelineRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.search, items.length]);

  const filtered = items.filter((i) => {
    const tagOk = activeTag === 'all' || (i.tags || []).includes(activeTag);
    const statusOk = activeStatus === 'all' || (i.status || 'not_contacted') === activeStatus;
    return tagOk && statusOk;
  });

  // Counts per status for the pipeline overview chips
  const statusCounts = items.reduce((acc, it) => {
    const s = it.status || 'not_contacted';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const remove = async (it) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/api/saved-suppliers/${it.id}`);
      toast.success(t('supplierUnsaved'));
      setSelectedIds((ids) => ids.filter((x) => x !== it.id));
      load();
    } catch (e) {
      toast.error(t('templateError'));
    }
  };

  const startEdit = (it) => {
    setEditing(it);
    setEditNotes(it.notes || '');
    setEditTags(it.tags || []);
    setEditStatus(it.status || 'not_contacted');
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      await api.put(`/api/saved-suppliers/${editing.id}`, {
        notes: editNotes,
        tags: editTags,
        status: editStatus,
      });
      toast.success(t('supplierSaved'));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(t('templateError'));
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleEditTag = (tag) => {
    setEditTags((prev) => prev.includes(tag) ? prev.filter((p) => p !== tag) : [...prev, tag]);
  };

  const toggleSelect = (id) => {
    setSelectedIds((ids) => {
      if (ids.includes(id)) return ids.filter((x) => x !== id);
      if (ids.length >= 3) return ids;
      return [...ids, id];
    });
  };

  const openMessage = (it) => {
    setMessageItem(it);
  };

  const closeMessage = () => {
    setMessageItem(null);
  };

  // Quickly bump status from a card without opening the edit dialog
  const quickSetStatus = async (it, status) => {
    try {
      await api.put(`/api/saved-suppliers/${it.id}`, { status });
      toast.success(t('statusUpdated'));
      load();
    } catch (e) {
      toast.error(t('templateError'));
    }
  };

  const selectedSuppliers = items
    .filter((i) => selectedIds.includes(i.id))
    .map((i) => i.supplier);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  // Export the currently-visible (filtered) saved suppliers as a PDF report.
  const exportPdf = async () => {
    if (filtered.length === 0) {
      toast.error(t('pdfEmptySaved'));
      return;
    }
    setExportingPdf(true);
    try {
      const { data } = await api.post(
        '/api/reports/saved/pdf',
        {
          saved_ids: filtered.map((i) => i.id),
          language: language === 'fr' ? 'fr' : 'en',
        },
        { responseType: 'blob' },
      );
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `safesourcer-saved-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('pdfReady'));
    } catch (e) {
      toast.error(t('pdfError'));
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="saved-page-title">{t('savedPageTitle')}</h1>
            <p className="text-sm text-slate-500 mt-1">{t('savedPageSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Button
                onClick={exportPdf}
                disabled={exportingPdf || filtered.length === 0}
                variant="outline"
                className="border-slate-300 hover:bg-slate-50"
                data-testid="saved-export-pdf"
              >
                {exportingPdf ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
                {exportingPdf ? t('exportingPdf') : t('exportPdf')}
              </Button>
            )}
            {selectedIds.length >= 2 && (
              <Button
                onClick={() => setCompareOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="saved-compare-button"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                {t('compareSelected')} ({selectedIds.length})
              </Button>
            )}
          </div>
        </div>

        {/* Status pipeline chips (Phase C) */}
        <div className="mb-3" data-testid="saved-pipeline" ref={pipelineRef}>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">{t('pipelineLabel')}</p>
          <div className="flex gap-1.5 overflow-x-auto pb-1 flex-nowrap whitespace-nowrap">
            <button
              type="button"
              onClick={() => setActiveStatus('all')}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                activeStatus === 'all'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="saved-status-all"
            >
              {t('allStatuses')} ({items.length})
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setActiveStatus(s)}
                className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                  activeStatus === s
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`saved-status-${s}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[s]}`} />
                {t(`status_${s}`)} ({statusCounts[s] || 0})
              </button>
            ))}
          </div>
        </div>

        {/* Tag filters */}
        <div className="flex gap-1.5 overflow-x-auto mb-4 pb-1" data-testid="saved-tags-bar">
          <button
            type="button"
            onClick={() => setActiveTag('all')}
            className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              activeTag === 'all'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid="saved-tag-all"
          >
            {t('allCategories')}
          </button>
          {PRESET_TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => setActiveTag(tag)}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                activeTag === tag
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`saved-tag-${tag}`}
            >
              {t(`tag_${tag}`)}
            </button>
          ))}
        </div>

        {/* List */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="saved-empty">
            <Bookmark className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">{t('noSavedSuppliers')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('noSavedSuppliersDesc')}</p>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="saved-grid">
            {filtered.map((it) => {
              const s = it.supplier || {};
              const total = s.score_breakdown?.total ?? s.reliability_score ?? 0;
              const checked = selectedIds.includes(it.id);
              const status = it.status || 'not_contacted';
              return (
                <div
                  key={it.id}
                  className={`bg-white border rounded-lg p-4 flex flex-col ${checked ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200'}`}
                  data-testid="saved-card"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 truncate">{s.name}</h3>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200">
                          {total}/100
                        </span>
                        <span
                          className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border flex items-center gap-1 ${STATUS_BADGE[status]}`}
                          data-testid="saved-status-badge"
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
                          {t(`status_${status}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                        {s.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</span>}
                        <span>·</span>
                        <span>{t('savedAt')} {formatDate(it.saved_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {t('forProduct')} <span className="text-slate-700 font-medium">{it.product_name}</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleSelect(it.id)}
                      disabled={!checked && selectedIds.length >= 3}
                      className={`shrink-0 w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${
                        checked
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-slate-300 text-slate-400 hover:border-blue-400 disabled:opacity-40'
                      }`}
                      aria-label={t('selectToCompare')}
                      data-testid="saved-select-checkbox"
                    >
                      {checked ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                    <div className="bg-slate-50 rounded px-2 py-1.5">
                      <div className="text-slate-500">{t('priceRange')}</div>
                      <div className="font-semibold text-slate-800">
                        {s.price_range_low && s.price_range_high
                          ? `$${s.price_range_low}–$${s.price_range_high}`
                          : `$${s.price ?? '—'}`}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded px-2 py-1.5">
                      <div className="text-slate-500">{t('moq')}</div>
                      <div className="font-semibold text-slate-800">{s.moq ?? '—'}</div>
                    </div>
                    <div className="bg-slate-50 rounded px-2 py-1.5">
                      <div className="text-slate-500">{t('leadTime')}</div>
                      <div className="font-semibold text-slate-800">{s.lead_time_days ? `${s.lead_time_days} ${t('days')}` : '—'}</div>
                    </div>
                  </div>

                  {/* Tags */}
                  {(it.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="saved-tags">
                      {it.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${TAG_COLORS[tag] || 'bg-slate-50 text-slate-600 border-slate-200'}`}
                        >
                          {t(`tag_${tag}`) || tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Notes */}
                  {it.notes && (
                    <p className="text-xs text-slate-600 italic line-clamp-2 mb-3" data-testid="saved-notes">"{it.notes}"</p>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-1.5 mt-auto">
                    <Button size="sm" variant="outline" onClick={() => openMessage(it)} data-testid="saved-message-btn">
                      <MessageSquare className="w-3.5 h-3.5 mr-1" />{t('generateMessage')}
                    </Button>
                    {status === 'not_contacted' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => quickSetStatus(it, 'contacted')}
                        className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        data-testid="saved-mark-contacted-btn"
                      >
                        <BadgeCheck className="w-3.5 h-3.5 mr-1" />{t('markAsContacted')}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid="saved-edit-btn">
                      <Edit2 className="w-3.5 h-3.5 mr-1" />{t('edit')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(it)} className="text-rose-600 hover:bg-rose-50" data-testid="saved-delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1" />{t('delete')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit dialog */}
      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent
          className="sm:max-w-md p-0 gap-0 h-screen max-h-screen w-screen rounded-none inset-0 translate-x-0 translate-y-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:max-h-[90vh] sm:w-auto sm:rounded-lg overflow-hidden flex flex-col"
          data-testid="saved-edit-dialog"
        >
          <DialogHeader className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200">
            <DialogTitle>{editing?.supplier?.name}</DialogTitle>
            <DialogDescription className="sr-only">{t('edit')}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('outreachStatus')}</Label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="saved-status-select"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`status_${s}`)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('notes')}</Label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder={t('notesPlaceholder')}
                className="min-h-[100px]"
                data-testid="saved-notes-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('addTag')}</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="saved-tags-picker">
                {PRESET_TAGS.map((tag) => {
                  const active = editTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleEditTag(tag)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`saved-tag-toggle-${tag}`}
                    >
                      {t(`tag_${tag}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200 p-4 sm:p-6 flex gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">{t('cancel')}</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="saved-save-edit">
              {savingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('saveChanges')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comparison */}
      <SupplierComparison
        open={compareOpen}
        onOpenChange={setCompareOpen}
        suppliers={selectedSuppliers}
        t={t}
        onGenerateMessage={(supplier) => {
          setCompareOpen(false);
          const match = items.find((i) => i.supplier?.name === supplier.name);
          if (match) openMessage(match);
        }}
      />

      {/* Message dialog */}
      <MessageDialog
        open={Boolean(messageItem)}
        onOpenChange={(o) => !o && closeMessage()}
        supplier={messageItem?.supplier}
        context={messageItem ? {
          product_name: messageItem.product_name,
          quantity: messageItem.quantity,
          budget: messageItem.budget,
        } : null}
        defaultLanguage={language === 'fr' ? 'fr' : 'en'}
        savedId={messageItem?.id}
        savedStatus={messageItem?.status}
        onStatusChanged={() => load()}
      />
    </div>
  );
};

export default Saved;
